import React, { useEffect, useState } from "react";
import { Form, Spinner } from "react-bootstrap";
import {
  getCamposPrograma,
  IOpcaoCampoPrograma,
} from '../../../../helpers/api/programa-conf'

enum ETipo {
  _,
  texto,
  texto_multilinha,
  selecao,
  data,
  booleano,
  intervalo_data,
  selecao_multipla
}

interface ICampoPrograma {
  id: number;
  nome: string;
  tipocampo_id: number;
  obrigatorio: boolean;
  ordem: number;
  configuracao: Record<string, any>;
  opcoes?: IOpcaoCampoPrograma[];
}

export interface IResposta {
  id?: number
  campoprograma_id: number;
  valor: any;
}

interface IProgramaDefinicaoCustomProps {
  respostas: IResposta[];
  setRespostas: Function
}

export default function ProgramaDefinicaoCustom({
  respostas,
  setRespostas,
}: IProgramaDefinicaoCustomProps) {
  const [campos, setCampos] = useState<ICampoPrograma[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    carregarCampos();
  }, []);

  async function carregarCampos() {
    try {
      const response = await getCamposPrograma();
      setCampos(response.data as ICampoPrograma[]);
    } finally {
      setLoading(false);
    }
  }

  function obterResposta(campoId: number) {
    return respostas.find(
      (r) => r.campoprograma_id === campoId,
    )?.valor;
  }

  function alterarResposta(
    campoId: number,
    valor: any,
  ) {
    setRespostas((prev) => {
      const existe = prev.some(
        (r) => r.campoprograma_id === campoId,
      );

      if (existe) {
        return prev.map((r) =>
          r.campoprograma_id === campoId
            ? { ...r, valor }
            : r,
        );
      }

      return [
        ...prev,
        {
          campoprograma_id: campoId,
          valor,
        },
      ];
    });
  }

  function renderCampo(campo: ICampoPrograma) {
    const valor = obterResposta(campo.id);

    switch (campo.tipocampo_id) {
      case ETipo.texto:
        return (
          <Form.Control
            type="text"
            value={valor ?? ""}
            required={campo.obrigatorio}
            onChange={(e) =>
              alterarResposta(
                campo.id,
                e.target.value,
              )
            }
          />
        );

      case ETipo.texto_multilinha:
        return (
          <Form.Control
            as="textarea"
            rows={4}
            value={valor ?? ""}
            required={campo.obrigatorio}
            onChange={(e) =>
              alterarResposta(
                campo.id,
                e.target.value,
              )
            }
          />
        );

      case ETipo.selecao:
        return (
          <Form.Select
            value={valor ?? ""}
            required={campo.obrigatorio}
            onChange={(e) =>
              alterarResposta(
                campo.id,
                e.target.value
                  ? Number(e.target.value)
                  : null,
              )
            }
          >
            <option value="">
              Selecione...
            </option>

            {campo.opcoes?.map((opcao) => (
              <option
                key={opcao.id}
                value={opcao.id}
              >
                {opcao.nome}
              </option>
            ))}
          </Form.Select>
        );

        case ETipo.selecao_multipla:
            const selecionados: number[] = Array.isArray(valor)
                ? valor
                : [];

            return (
                <>
                {campo.opcoes?.map((opcao) => (
                    <Form.Check
                    key={opcao.id}
                    type="checkbox"
                    id={`campo-${campo.id}-opcao-${opcao.id}`}
                    label={opcao.nome}
                    checked={selecionados.includes(opcao.id!)}
                    onChange={(e) => {
                        let novoValor: number[];

                        if (e.target.checked) {
                        novoValor = [
                            ...selecionados,
                            opcao.id!,
                        ];
                        } else {
                        novoValor = selecionados.filter(
                            (id) => id !== opcao.id
                        );
                        }

                        alterarResposta(
                        campo.id,
                        novoValor
                        );
                    }}
                    />
                ))}
                </>
            );

      case ETipo.data:
        return (
          <Form.Control
            type="date"
            value={valor ?? ""}
            required={campo.obrigatorio}
            onChange={(e) =>
              alterarResposta(
                campo.id,
                e.target.value,
              )
            }
          />
        );

      case ETipo.booleano:
        return (
          <Form.Check
            type="switch"
            checked={Boolean(valor)}
            onChange={(e) =>
              alterarResposta(
                campo.id,
                e.target.checked,
              )
            }
            label=""
          />
        );

      case ETipo.intervalo_data:
        return (
          <div className="d-flex gap-2">
            <Form.Control
              type="date"
              value={valor?.inicio ?? ""}
              required={campo.obrigatorio}
              onChange={(e) =>
                alterarResposta(
                  campo.id,
                  {
                    inicio: e.target.value,
                    fim: valor?.fim ?? "",
                  },
                )
              }
            />

            <Form.Control
              type="date"
              value={valor?.fim ?? ""}
              required={campo.obrigatorio}
              onChange={(e) =>
                alterarResposta(
                  campo.id,
                  {
                    inicio:
                      valor?.inicio ?? "",
                    fim: e.target.value,
                  },
                )
              }
            />
          </div>
        );

      default:
        return null;
    }
  }

  if (loading) {
    return (
      <div className="text-center py-3">
        <Spinner animation={"border"} />
      </div>
    );
  }

  if (!campos.length) {
    return null;
  }

  return (
    <>
      {campos.map((campo) => (
        <Form.Group
          key={campo.id}
          className="mb-3"
        >
          <Form.Label>
            {campo.nome}

            {campo.obrigatorio && (
              <span className="text-danger">
                {" "}
                *
              </span>
            )}
          </Form.Label>

          {renderCampo(campo)}
        </Form.Group>
      ))}
    </>
  );
}